import { createContext, useContext, useMemo, type ReactNode, CSSProperties } from 'react'
import type { AgentThemeTokens } from '../config/types'
import { mergeTheme } from './defaultTheme'

const ThemeContext = createContext<AgentThemeTokens>(mergeTheme())

type ThemeCSSVariables = CSSProperties & Record<`--mp-agents-${string}`, string | number>

const asCssVars = (tokens: AgentThemeTokens): ThemeCSSVariables =>
  ({
    '--mp-agents-bg': tokens.background,
    '--mp-agents-surface': tokens.surface,
    '--mp-agents-text': tokens.text,
    '--mp-agents-subtext': tokens.subtext,
    '--mp-agents-border': tokens.border,
    '--mp-agents-accent': tokens.accent,
    '--mp-agents-accent-soft': tokens.accentSoft,
    '--mp-agents-warning': tokens.warning,
    '--mp-agents-danger': tokens.danger,
    '--mp-agents-radius': tokens.radius,
    '--mp-agents-font-size': tokens.fontSize
  } as ThemeCSSVariables)

export interface AgentThemeProviderProps {
  value?: Partial<AgentThemeTokens>
  children: ReactNode
}

export const AgentThemeProvider = ({ value, children }: AgentThemeProviderProps) => {
  const merged = useMemo(() => mergeTheme(value), [value])

  return (
    <ThemeContext.Provider value={merged}>
      <style>
        {`:root {
          ${Object.entries(asCssVars(merged))
            .map(([key, value]) => `${key}: ${value};`)
            .join('\n')}
          }`}
      </style>
      {children}
    </ThemeContext.Provider>
  )
}

export const useAgentTheme = () => useContext(ThemeContext)
